import React from "react";
import { Formik, Field, FieldProps, FormikHelpers } from "formik";
import { Select, Switch } from "antd";
import * as S from "./styles";
import {
  launchAdjustmentFormSchema,
  LaunchAdjustmentFormSchema,
  MAX_MIN_DATES,
} from "./schema";
import { InputWrapper } from "../../components/InputWrapper";
import { DSInput } from "../../components/DSInput";
import DSButton from "../../components/DSButton";
import { TextArea } from "../../components/TextArea";
import { ClientData } from "../ClientSelectionItem/types";
import { hasProcessorPermission } from "../../helpers/hasProcessorPermission";
import { formatCalendarDay } from "../../utils/formatDate";

const { Option } = Select;

type TypesAdjustments = {
  id: string;
  descricao: string;
};

type LaunchAdjustmentFormProps = {
  loadingTypes: boolean;
  typesAdjustments: TypesAdjustments[];
  clientInfo: ClientData;
  hasPermissionToUsePreviousDate: boolean;
  onSubmit: (
    values: LaunchAdjustmentFormSchema,
    helpers: FormikHelpers<LaunchAdjustmentFormSchema>
  ) => Promise<void> | void;
  onReset: () => void;
};

export function LaunchAdjustmentForm({
  loadingTypes,
  typesAdjustments,
  clientInfo,
  hasPermissionToUsePreviousDate,
  onSubmit,
  onReset,
}: LaunchAdjustmentFormProps) {
  const savedData = sessionStorage.getItem("@SAC_RPE_PERMISSION_MANAGEMENT");

  const permissionManagement = savedData
    ? JSON.parse(savedData)
    : { currentProcessor: undefined, apiData: [] };
  const today = formatCalendarDay(new Date());
  const hasRetroactiveProcessorPermission = hasProcessorPermission(
    "retroactive",
    permissionManagement?.currentProcessor
  );

  const initialValues: LaunchAdjustmentFormSchema = {
    type: "0",
    billingDate:
      hasRetroactiveProcessorPermission && hasPermissionToUsePreviousDate
        ? ""
        : today,
    value: "",
    activeInvoice: false,
    description: "",
  };

  const onChangeValue = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: FormikHelpers<LaunchAdjustmentFormSchema>["setFieldValue"]
  ) => {
    const { value } = event.target;

    const numericValue = value.replace(/\D/g, "");

    const integerPart = numericValue.slice(0, -2) || "0";
    const decimalPart = numericValue.slice(-2).padStart(2, "0");

    const formattedValue = `${parseInt(integerPart, 10).toLocaleString(
      "pt-BR"
    )},${decimalPart}`;

    setFieldValue("value", `R$ ${formattedValue}`);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={launchAdjustmentFormSchema}
      onSubmit={onSubmit}
      onReset={onReset}
    >
      {({ isSubmitting, handleReset, handleSubmit, setFieldValue }) => (
        <S.CustomForm onSubmit={handleSubmit} onReset={handleReset} noValidate>
          <S.ClientInfoContainer>
            <S.Label>Conta</S.Label>
            <S.CustomClientSelectionItem
              client={clientInfo}
              isSelected
              infosToShow={{
                cpf: false,
                id: true,
                name: true,
                statusCliente: true,
              }}
            />
          </S.ClientInfoContainer>

          <Field name="type">
            {({ field, form, meta }: FieldProps<string>) => (
              <InputWrapper
                label="Tipo de ajuste:"
                error={meta.touched && meta.error ? meta.error : undefined}
                name={field.name}
                isRequired
              >
                <S.CustomSelect
                  {...field}
                  value={loadingTypes ? undefined : field.value}
                  id={field.name}
                  className={
                    meta.touched && meta.error ? "error-id" : undefined
                  }
                  showSearch
                  optionFilterProp="children"
                  notFoundContent="Nenhum ajuste encontrado para esta busca!"
                  filterOption={(input, option) =>
                    String(option?.children)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(value) => form.setFieldValue(field.name, value)}
                  onBlur={() => form.setFieldTouched(field.name, true)}
                  style={{ width: "100%" }}
                  disabled={isSubmitting || loadingTypes}
                  loading={loadingTypes}
                  placeholder={
                    loadingTypes
                      ? "Carregando tipos de ajustes..."
                      : "Selecione o tipo de ajuste"
                  }
                  data-testid="select_tipo_ajuste"
                >
                  <Option value="0">Selecionar</Option>
                  {typesAdjustments.map((types) => (
                    <Option value={types.id} key={`$tipos-ajustes-${types.id}`}>
                      {types.descricao}
                    </Option>
                  ))}
                </S.CustomSelect>
              </InputWrapper>
            )}
          </Field>

          {hasRetroactiveProcessorPermission && (
            <Field name="billingDate">
              {({ field, meta }: FieldProps<string>) => (
                <InputWrapper
                  label="Data do faturamento"
                  error={meta.touched && meta.error ? meta.error : undefined}
                  name={field.name}
                  isRequired
                >
                  <DSInput
                    {...field}
                    type="date"
                    placeholder="dd/mm/yyyy"
                    touched={meta.touched}
                    error={!!meta.error}
                    disabled={isSubmitting || !hasPermissionToUsePreviousDate}
                    min={MAX_MIN_DATES.MIN.value}
                    max={MAX_MIN_DATES.MAX.value}
                    onChange={(e) => {
                      if (!hasPermissionToUsePreviousDate) {
                        return;
                      }

                      field.onChange(e);
                    }}
                    data-testid="input_data_faturamento"
                  />
                </InputWrapper>
              )}
            </Field>
          )}

          <Field name="value">
            {({ field, meta }: FieldProps<string>) => (
              <InputWrapper
                label="Valor"
                error={meta.touched && meta.error ? meta.error : undefined}
                name={field.name}
                isRequired
              >
                <DSInput
                  {...field}
                  placeholder="Valor(R$)"
                  touched={meta.touched}
                  error={!!meta.error}
                  disabled={isSubmitting}
                  onChange={(event) => onChangeValue(event, setFieldValue)}
                  data-testid="input_valor"
                />
              </InputWrapper>
            )}
          </Field>

          <Field name="description">
            {({ field, meta }: FieldProps<string>) => (
              <TextArea
                label="Descrição"
                value={field.value}
                onChange={field.onChange}
                error={meta.touched && meta.error ? meta.error : ""}
                maxLength={255}
                name={field.name}
                onBlur={field.onBlur}
                isRequired
                disabled={isSubmitting}
                data-testid="input_descricao"
              />
            )}
          </Field>

          <S.CheckboxContainer>
            <Field name="activeInvoice">
              {({ field, form }: FieldProps<boolean>) => (
                <Switch
                  {...field}
                  checked={field.value}
                  onChange={(checked) =>
                    form.setFieldValue(field.name, checked)
                  }
                  disabled={isSubmitting}
                  data-testid="switch_fatura_ativa"
                />
              )}
            </Field>
            <span>Incidir na fatura ativa</span>
          </S.CheckboxContainer>

          <S.ButtonsWrapper>
            <DSButton
              type="reset"
              buttonText="Cancelar"
              buttonType="secondary"
              isCancelType
              onClick={handleReset}
              height={30}
              style={{
                padding: "0.4rem 1.5rem",
              }}
              isDisabled={isSubmitting}
              disabled={isSubmitting}
              dataTestId="botao_cancelar_ajuste"
            />
            <DSButton
              type="submit"
              buttonText="Confirmar"
              buttonType="secondary"
              height={30}
              style={{
                padding: "0.4rem 1.5rem",
              }}
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
              disabled={isSubmitting}
              dataTestId="botao_confirmar_ajuste"
            />
          </S.ButtonsWrapper>
        </S.CustomForm>
      )}
    </Formik>
  );
}
